// Moves focus into an AppDialog's Panel on open. `this` is the AppDialog
// ROOT element (called from its own `isOpen` factory via `el.call`, deferred
// a frame so the Panel's `hide:`/visibility flip has already landed in the
// DOM) — `el.node` is the root's DOM node, and the Panel is its only
// focusable descendant (Backdrop has none), so a plain `querySelectorAll`
// from the root finds the right candidates without needing a separate
// Panel-scoped call. Reads `el.node` to find the first VISIBLE focusable
// descendant — a read, then a `.focus()` call, both allowed on `el.node`
// per Rule 39; no assignment to `.style`/`.innerHTML`/`.value` anywhere
// here.
//
// `offsetParent !== null` filters out any candidate sitting inside a
// `hide:`-gated (display:none) ancestor — e.g. a conditionally-hidden
// "Cancel" button that renders earlier in DOM order than the always-visible
// "Close" button, or a checkbox inside a `hide`-gated confirmation row
// (NatOrderCancelModal's `ManagerRow`, gated on `needsManager`). A plain
// `querySelector` picks the first DOM-order match regardless of visibility,
// and `.focus()` on a `display:none` element silently no-ops — leaving
// focus wherever it was before the dialog opened. Caught live (scheduling
// app migration, design-agent, tickets/workspace.md a11y ticket) driving
// `NatAppointmentDetailModal`/`NatOrderCancelModal` open in a real browser
// and asserting on `document.activeElement`, not from a source-only review.
// `appDialogTrapTab.js`'s Tab-cycle already used this exact filter — this
// brings the two sibling functions back in sync.
//
// `initialFocus` (AppDialog v2, DESIGN_SPEC §6.6):
//   'field' — the first enabled FORM control (a form: type straight away);
//             falls back to the first focusable, then to the panel
//   'panel' — the Panel itself (`tabindex: -1`): a read-only detail, where
//             landing on its first control (the Close button) made a screen
//             reader skip the title
//   default — 'panel' for a side panel, 'field' for a centre sheet.
// `preventScroll`: the panel is mid-entrance (transformed); a focus scroll
// would jerk the frame behind it.
export function appDialogFocusPanel() {
  const el = this
  if (typeof document === 'undefined' || !el.node) return
  const panel = el.Panel && el.Panel.node
  const side = el.presentation === 'side' || (!el.presentation && el.anchor === 'right')
  const mode = el.initialFocus || (side ? 'panel' : 'field')
  const focus = (n) => {
    try {
      n.focus({ preventScroll: true })
    } catch (e) {
      n.focus()
    }
  }
  if (mode === 'panel' && panel && panel.focus) return focus(panel)
  const visible = (n) => n.offsetParent !== null
  const fields = el.node.querySelectorAll(
    'input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [contenteditable="true"]'
  )
  const field = Array.prototype.find.call(fields, visible)
  if (field && field.focus) return focus(field)
  const nodeList = el.node.querySelectorAll(
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
  )
  const focusable = Array.prototype.find.call(nodeList, visible)
  if (focusable && focusable.focus) focus(focusable)
  else if (panel && panel.focus) focus(panel)
  else if (el.node.focus) focus(el.node)
}
